import React, { useRef } from 'react'
import { useEffect, useState } from 'react'
import axios from 'axios'

import {
  Typography,
  Badge,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  DialogContent,
  Tooltip,
  Box,
  TableContainer,
  TableHead,
  TableCell,
  Table,
  TableRow,
  Paper,
  TableBody,
  CircularProgress,
  Popover,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { GetApp as GetAppIcon } from '@mui/icons-material'
import { useAuthContext } from '../../hooks/useAuthContext'
import { HighlightOff as HighlightOffIcon } from '@mui/icons-material'
import {
  ACTIVITY_TYPES,
  PROCESS_ACTIVITY_STATUSES,
  timelineColorCode,
} from '../../utility/constants'
import moment from 'moment'
import DocumentDialogBox from './DocumentDialogBox'

import DataPreview from '../DataPreview/DataPreview'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import ProcessFlowViewer from './ProcessFlowViewer'
import ParallelRouteTimeline from '../Timeline/ParallelRouteTimeline'

const ProcessTimeline = ({
  processReferanceNum,
  processTemplateId,
  task,
  templateRoutes,
  processTemplates,
  processTimeLineBadge = 0,
  showInTaskCard = false,
  showInTBD = false,
}) => {
  const { user } = useAuthContext()
  const contentRef = useRef()

  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

  let axiosOptions = {}
  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` },
    }
  }

  const [timeLineOpen, setTimelineOpen] = useState(false)
  const [activityDataOpen, setActivityDataOpen] = useState(false)
  const [processId, setProcessId] = useState()
  const [showTableData, setShowTableData] = useState(false)
  const [tableHeadArr, setTableHeadArr] = useState([])
  const [tableDataArr, setTableDataArr] = useState([])
  const [processName, setProcessName] = useState('')
  const [loading, setLoading] = useState(false)
  const [isDocOpen, setIsDocOpen] = useState(false)
  const [docData, setDocData] = useState(null)
  const [updatedTimeline, setUpdatedTimeline] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [viewOptions, setViewOptions] = useState('timeline')
  const [infoPopOverAnchorEl, setInfoPopOverAnchorEl] = React.useState(null)

  const handleViewChange = (event) => {
    const selectedView = event.target.value
    setViewOptions(selectedView)
    switch (selectedView) {
      case 'timeline':
        setShowTableData(false)
        break
      case 'table':
        setShowTableData(true)
        break
      case 'processflow':
        setShowTableData(false)
        break
      default:
        setShowTableData(false)
    }
  }

  const handleInfoPopoverOpen = (event) => {
    setInfoPopOverAnchorEl(event.currentTarget)
  }

  const handleInfoPopoverClose = () => {
    setInfoPopOverAnchorEl(null)
  }

  const open = Boolean(infoPopOverAnchorEl)

  const DocStyle = {
    color: '#0000EE',
    cursor: 'pointer',
  }

  // const getTimeLineColorCode = (activity) => {
  //   if (activity.status == 'delegated') {
  //     return timelineColorCode['delegation']
  //   } else if (
  //     activity.is_parallel_activity &&
  //     activity.activity_type == ACTIVITY_TYPES.DATA_ENTRY
  //   ) {
  //     return timelineColorCode['parallel-data-entry']
  //   } else if (activity.is_parallel_activity && activity.activity_type == ACTIVITY_TYPES.REVIEW) {
  //     return timelineColorCode['parallel-review']
  //   } else if (activity.activity_type == ACTIVITY_TYPES.END_ACCEPTED) {
  //     return timelineColorCode['end-accepted']
  //   } else {
  //     return timelineColorCode[activity.activity_type]
  //   }
  // }

  useEffect(() => {
    let getTimelineData = async () => {
      const detailedDataByReferanceNum = (
        await axios.get(
          `${NODE_BASE_URL}/data-entry/process-data/detailed-process-data/${processReferanceNum}/${processTemplateId}`,
          axiosOptions
        )
      ).data

      let processName = `${detailedDataByReferanceNum[0]?.prefix}-${processReferanceNum}`

      setProcessName(processName)

      return detailedDataByReferanceNum
    }
    if (timeLineOpen) {
      ;(async () => {
        setLoading(true)

        const promiseResult = await Promise.all([
          getTimelineData(),
          fetchDelegationData(),
          getDocument(),
        ])

        createTimelineData(promiseResult[0], promiseResult[1], promiseResult[2])
        setLoading(false)
      })()
    }
  }, [timeLineOpen])

  const createTimelineData = (dataByReferanceNum, delegationTrackerData, tempDocuments) => {
    const reducedArray =
      dataByReferanceNum?.reduce((acc, cur) => {
        let reorderedDelegatedTimeline = []
        let updatedCurrent = cur
        const delegationData = delegationTrackerData.filter((tl) => {
          return tl.process_id == cur.id
        })
        if (delegationData.length > 0) {
          // sorting in descending order
          delegationData.sort((a, b) => {
            return new Date(a.delegated_at) - new Date(b.delegated_at)
          })

          const latestTime = delegationData[delegationData.length - 1]?.delegated_at
          let startTimeActual = cur.created_at

          for (let index = 0; index < delegationData.length; index++) {
            if (index == 0) {
              reorderedDelegatedTimeline.push({
                ...delegationData[0],
                created_at: startTimeActual,
                status: 'delegated',
              })
            } else {
              reorderedDelegatedTimeline.push({
                ...delegationData[index],
                created_at: delegationData[index - 1]?.delegated_at,
                status: 'delegated',
              })
            }
          }

          updatedCurrent = { ...cur, created_at: latestTime }
        }

        return [...acc, updatedCurrent, ...reorderedDelegatedTimeline]
      }, []) || []

    const timelineData = [...reducedArray]
    timelineData.sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at)
    })

    const updatedTimeline = timelineData.map((process, index) => {
      const allDocsForTask = (tempDocuments || [])?.filter((data) => data.process_id == process.id)
      return {
        ...process,
        document: allDocsForTask || [],
      }
    })
    console.log('UpdatedTimeline:', updatedTimeline)

    setUpdatedTimeline(updatedTimeline)

    let tableHeadArr = [
      { name: 'Activity', width: 20 },
      { name: 'Assignee', width: 20 },
      { name: 'Vote', width: 20 },
      { name: 'Status', width: 15 },
      { name: 'Start Date', width: 20 },
      { name: 'Close Date', width: 20 },
      { name: 'Overall Comments', width: 60 },
    ]

    let tableDataArr = []

    updatedTimeline?.forEach((refData) => {
      let tableRow = []

      if (refData.is_parallel_activity && Array.isArray(refData.activities)) {
        // Loop through each parallel activity
        refData.activities.forEach((activity) => {
          let parallelRow = []
          parallelRow.push(
            activity.activity_template_name,
            `${activity.first_name} ${activity.last_name}`,
            (templateRoutes &&
              templateRoutes?.filter((item) => activity?.to_route_template_id == item.id))[0]
              ?.route_template_name,
            activity.status,
            moment(new Date(activity.created_at)).format('DD MMM YYYY, hh:mm A'),
            moment(
              new Date(
                activity.status === 'delegated' ? activity.delegated_at : activity.modified_at
              )
            ).format('DD MMM YYYY, hh:mm A'),
            activity.overall_comments || activity.comments
          )
          tableDataArr.push(parallelRow)
        })
      } else {
        // Handle non-parallel activities
        tableRow?.push(
          refData.activity_template_name,
          `${refData.first_name} ${refData.last_name}`,
          (templateRoutes &&
            templateRoutes?.filter((item) => refData?.to_route_template_id == item.id))[0]
            ?.route_template_name,
          refData.status,
          moment(new Date(refData.created_at)).format('DD MMM YYYY, hh:mm A'),
          moment(
            new Date(refData.status === 'delegated' ? refData.delegated_at : refData.modified_at)
          ).format('DD MMM YYYY, hh:mm A'),
          refData.overall_comments || refData.comments
        )
        tableDataArr.push(tableRow)
      }
    })

    setTableHeadArr(tableHeadArr)

    setTableDataArr(tableDataArr)
  }

  const fetchDelegationData = async () => {
    try {
      const response = await axios.get(
        `${NODE_BASE_URL}/data-entry/delegation/${processTemplateId}/${processReferanceNum}`,
        axiosOptions
      )
      const result = response.data
      return result
    } catch (error) {
      console.error('Error fetching delegation data:', error)
    }
  }

  const getDocument = async () => {
    const tempDoc = (
      await axios.get(
        `${NODE_BASE_URL}/data-entry/process-data/template-doc/${processReferanceNum}/${processTemplateId}`,
        axiosOptions
      )
    ).data
    return tempDoc
  }

  // let getDocumentData = async (process) => {
  //   setIsDocOpen(true)
  //   setTimelineOpen(false)
  //   loadFormDataAndAllValues(process)
  //   setDocData(process?.document)
  // }

  const loadFormDataAndAllValues = async (dd) => {
    setProcessId(dd.id)
  }

  function calculateDuration(startDate, endDate) {
    const date1 = moment(startDate)
    const date2 = moment(endDate)

    let diffInMinutes = date1.diff(date2, 'minutes')

    if (diffInMinutes <= 0) {
      diffInMinutes = 0
    }
    diffInMinutes = Math.floor(diffInMinutes)

    let minute = diffInMinutes % 60
    let days = Math.floor(diffInMinutes / (60 * 24))
    let hours = Math.floor(diffInMinutes / 60)
    hours -= days * 24

    let timeStr = ''

    if (days == 0 && hours == 0 && minute == 0) timeStr = `1m`
    else if (days == 0 && hours == 0) timeStr = `${minute}m`
    else if (days == 0 && hours != 0) timeStr = `${hours}h ${minute}m`
    else {
      timeStr = `${days}d ${hours}h ${minute}m`
    }
    return timeStr
  }

  const getDuration = (dd) => {
    switch (dd.status) {
      case PROCESS_ACTIVITY_STATUSES.ACTIVE:
        return calculateDuration(
          // /TODO: need to chenge time for localhost
          //converting client datetime to utc datetime (note: DB timestamps are in utc)
          new Date(new Date().getTime()),
          dd.created_at
        )
      case PROCESS_ACTIVITY_STATUSES.DELEGATED:
        return calculateDuration(dd.delegated_at, dd.created_at)
      default:
        return calculateDuration(dd.modified_at, dd.created_at)
    }
  }

  const extractTimelineInfo = (updatedTimeline) => {
    const currentStatus = updatedTimeline?.[updatedTimeline.length - 1]?.status ?? 'Unknown'
    const initiatedOn = updatedTimeline?.[0]?.created_at ?? 'Unknown'
    const lastObject = updatedTimeline?.[updatedTimeline.length - 1]
    const closedOn = lastObject?.status === 'closed' ? lastObject.modified_at : ''

    return {
      currentStatus,
      initiatedOn,
      closedOn,
    }
  }

  const timeLinePdfData = extractTimelineInfo(updatedTimeline)

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return
    setIsGenerating(true)

    try {
      const clone = contentRef.current.cloneNode(true)
      const wrapper = document.createElement('div')
      wrapper.style.position = 'fixed'
      wrapper.style.top = '0'
      wrapper.style.left = '-10000px'
      wrapper.style.width = '1000px'
      wrapper.style.zIndex = '-1'
      wrapper.appendChild(clone)
      document.body.appendChild(wrapper)

      const fixAllHeights = (element) => {
        element.style.maxHeight = 'none'
        element.style.height = 'auto'
        element.style.overflow = 'visible'
        Array.from(element.children).forEach(fixAllHeights)
      }
      fixAllHeights(clone)

      await document.fonts.ready
      const images = clone.querySelectorAll('img')
      await Promise.all(
        Array.from(images).map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve, reject) => {
                img.onload = resolve
                img.onerror = reject
              })
        )
      )

      const canvas = await html2canvas(clone, {
        useCORS: true,
        scale: 2,
        windowWidth: document.body.scrollWidth,
        windowHeight: clone.scrollHeight,
      })

      const imgData = canvas.toDataURL('image/jpeg', 1.0)
      const imgWidth = canvas.width
      const imgHeight = canvas.height

      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const pxPerMm = imgWidth / pdfWidth
      const pageHeightPx = pdfHeight * pxPerMm
      const pageCount = Math.ceil(imgHeight / pageHeightPx)

      for (let page = 0; page < pageCount; page++) {
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = imgWidth
        pageCanvas.height = Math.min(pageHeightPx, imgHeight - page * pageHeightPx)

        const ctx = pageCanvas.getContext('2d')
        ctx.drawImage(
          canvas,
          0,
          page * pageHeightPx,
          imgWidth,
          pageCanvas.height,
          0,
          0,
          imgWidth,
          pageCanvas.height
        )

        const pageData = pageCanvas.toDataURL('image/jpeg', 1.0)
        if (page > 0) pdf.addPage()
        pdf.addImage(pageData, 'JPEG', 0, 0, pdfWidth, pageCanvas.height / pxPerMm)
      }

      pdf.save(`${processName || 'report'}.pdf`)
      document.body.removeChild(wrapper)
    } catch (err) {
      console.error('PDF generation error:', err)
    } finally {
      setIsGenerating(false)
    }
  }

  // Add this more minimal filtering function
  const getMinimalTimelineForFlow = (timeline) => {
    return (
      timeline?.map((item) => ({
        id: item.id,
        first_name: item.first_name,
        last_name: item.last_name,
        created_at: item.created_at,
        activity_template_id: item.activity_template_id,
        activity_template_name: item.activity_template_name,
        activity_type: item.activity_type,
        status: item.status,
        modified_by: item.modified_by, 
        version: item.version, 
        isCurrentPosition: item.status === 'active', 
      })) || []
    )
  }

  return (
    <>
      {' '}
      {/* {dataByReferanceNum && ( */}
      <>
        {showInTBD ? (
          <Tooltip title={'View Requisition Progress'}>
            <IconButton size="small" onClick={() => setTimelineOpen(true)}>
              <InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />
            </IconButton>
          </Tooltip>
        ) : showInTaskCard ? (
          <div
            className="d-flex align-item-center justify-content-center"
            onClick={() => {
              setTimelineOpen(true)
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />
            <span style={{ fontSize: '11px', color: '#484A54', margin: '0px 7px' }}>Timeline</span>
          </div>
        ) : (
          <Tooltip title={'Process Timeline'}>
            <IconButton
              onClick={() => {
                setTimelineOpen(true)
              }}
              color={task && 'info'}
              size="small"
              sx={{ padding: '2px' }}
            >
              <Badge
                className="timeline-badge"
                color="primary"
                overlap="rectangular"
                badgeContent={processTimeLineBadge}
                sx={{
                  transform: 'translate(25px, -11px) !important',
                }}
              />
              <InfoOutlinedIcon sx={{ fontSize: '17px !important' }} />
            </IconButton>
          </Tooltip>
        )}

        <Dialog
          open={timeLineOpen}
          onClose={() => {
            setTimelineOpen(false)
            setShowTableData(false)
          }}
          sx={{
            '& .MuiDialog-paper': {
              width: '100%',
              maxWidth: '1000px',
              '@media (min-width:1280px)': {
                width: '80%',
                maxWidth: '1000px',
              },
              '@media (min-width:1920px)': {
                width: '60%',
                maxWidth: '1400px',
              },
            },
          }}
        >
          {loading ? (
            <>
              <Box
                className="timeline-section"
                minWidth={'40vw'}
                minHeight={'40vh'}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <CircularProgress color="primary" />
              </Box>
            </>
          ) : (
            <>
              <DialogTitle style={{ padding: '5px 24px', marginTop: '10px' }}>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" className="fontWeight800 textprimaryblue fontsize14">
                      {processName}
                    </Typography>

                    <FormControl sx={{ minWidth: 140 }} size="small">
                      <Select
                        labelId="view-options-label"
                        id="view-options-select"
                        value={viewOptions}
                        onChange={handleViewChange}
                      >
                        <MenuItem value="timeline">Timeline View</MenuItem>
                        <MenuItem value="table">Table View</MenuItem>
                        <MenuItem value="processflow">Process Flow View</MenuItem>
                      </Select>
                    </FormControl>

                    {viewOptions === 'timeline' && (
                      <IconButton
                        aria-owns={open ? 'mouse-over-popover' : undefined}
                        aria-haspopup="true"
                        onMouseEnter={handleInfoPopoverOpen}
                        onMouseLeave={handleInfoPopoverClose}
                        size="small"
                      >
                        {/*Feature Commented as of now */}

                        {/* <InfoOutlinedIcon
                          sx={{
                            fontSize: '18px !important',
                            backgroundColor: '#eaeeff',
                            borderRadius: '13px',
                          }}
                        /> */}
                      </IconButton>
                    )}

                    {/* Popover for timeline view only */}
                    {viewOptions === 'timeline' && (
                      <Popover
                        id="mouse-over-popover"
                        sx={{
                          pointerEvents: 'none',
                          boxShadow: '1px 2px 6px 0px rgb(57 85 120 / 11%) !important',
                        }}
                        open={open}
                        anchorEl={infoPopOverAnchorEl}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'center',
                        }}
                        onClose={handleInfoPopoverClose}
                        disableRestoreFocus
                      >
                        <div
                          style={{
                            padding: '15px',
                            width: '20vw',
                            backgroundColor: '#fff',
                            border: '1px solid #EDF0FF',
                          }}
                        >
                          <fieldset
                            style={{
                              borderRadius: '5px',
                              boxSizing: 'border-box',
                              textTransform: 'capitalize',
                            }}
                          >
                            <legend>
                              <Typography className="fontWeight800" variant="subtitle2">
                                Task Type
                              </Typography>
                            </legend>
                            <div>
                              {Object.entries(timelineColorCode).map(([key, value], index) => (
                                <div
                                  key={index}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '10px',
                                    borderBottom: '1px dashed #ddd',
                                    fontWeight: '600',
                                    fontSize: '13px',
                                  }}
                                >
                                  <Typography variant="body2">{key}</Typography>
                                  <div
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: value,
                                      borderRadius: '50%',
                                    }}
                                  ></div>
                                </div>
                              ))}
                            </div>
                          </fieldset>
                        </div>
                      </Popover>
                    )}
                  </Box>

                  <Box className="timeline-switch" alignItems="center" display={'flex'}>
                    {showTableData && (
                      <Tooltip title="Download data in Pdf format">
                        <IconButton size="large" onClick={handleDownloadPDF}>
                          {isGenerating ? (
                            <CircularProgress color="primary" size={24} />
                          ) : (
                            <GetAppIcon color="primary" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      align="right"
                      onClick={() => {
                        setTimelineOpen(false)
                        setShowTableData(false)
                        setViewOptions('timeline')
                      }}
                      size="large"
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </DialogTitle>
              {showTableData ? (
                <DialogContent ref={contentRef}>
                  <div
                    className="process-timelinetable"
                    style={{
                      padding: 20,
                      backgroundColor: '#F5F7FF',
                      overflow: 'visible',
                      height: 'auto',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        columnGap: 3,
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          rowGap: 3,
                          flexBasis: 1,
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ display: 'flex', width: '100%' }}>
                          <Typography
                            style={{ display: 'flex', fontWeight: 'bold', fontSize: '13px' }}
                          >
                            Process:<span style={{ marginLeft: '3px' }}></span>
                          </Typography>

                          <Typography
                            className="fontWeight600 textSecondary fontsize12"
                            style={{ display: 'flex', textTransform: 'capitalize' }}
                          >
                            {processTemplates?.process_template_name}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                          <Typography
                            style={{
                              display: 'flex',
                              marginLeft: '40px',
                              fontWeight: 'bold',
                              fontSize: '14px',
                            }}
                          >
                            Reference number:<span style={{ marginLeft: '3px' }}></span>
                          </Typography>
                          <Typography
                            className="fontWeight600 textSecondary fontsize12"
                            style={{ display: 'flex', textTransform: 'capitalize' }}
                          >
                            {processName}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                          <Typography
                            style={{
                              display: 'flex',
                              marginLeft: '40px',
                              fontWeight: 'bold',
                              fontSize: '14px',
                            }}
                          >
                            Current Status:<span style={{ marginLeft: '3px' }}></span>
                          </Typography>
                          <Typography
                            className="fontWeight600 textSecondary fontsize12"
                            style={{ display: 'inline', textTransform: 'capitalize' }}
                          >
                            {timeLinePdfData.currentStatus}
                          </Typography>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          rowGap: 3,
                        }}
                      >
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                          <Typography
                            style={{ display: 'flex', fontWeight: 'bold', fontSize: '13px' }}
                          >
                            Initiated By:<span style={{ marginLeft: '3px' }}></span>
                          </Typography>
                          <Typography
                            className="fontWeight600 textSecondary fontsize12"
                            style={{ display: 'flex', textTransform: 'capitalize' }}
                          >
                            {`${updatedTimeline?.[0]?.first_name} ${updatedTimeline?.[0]?.last_name}`}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                          <Typography
                            style={{ marginLeft: '40px', fontWeight: 'bold', fontSize: '13px' }}
                          >
                            Initiated On:<span style={{ marginLeft: '3px' }}></span>
                          </Typography>
                          <Typography
                            className="fontWeight600 textSecondary fontsize12"
                            style={{ display: 'flex', textTransform: 'capitalize' }}
                          >
                            {moment(new Date(timeLinePdfData.initiatedOn)).format(
                              'DD MMM YYYY, h:mm A'
                            )}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                          <Typography
                            style={{
                              display: 'flex',
                              marginLeft: '40px',
                              fontWeight: 'bold',
                              fontSize: '14px',
                            }}
                          >
                            Closed On:<span style={{ marginLeft: '3px' }}></span>
                          </Typography>
                          <Typography
                            className="fontWeight600 textSecondary fontsize12"
                            style={{ display: 'flex', textTransform: 'capitalize' }}
                          >
                            {moment(new Date(timeLinePdfData.closedOn)).format(
                              'DD MMM YYYY, h:mm A'
                            ) == 'Invalid date'
                              ? ''
                              : moment(new Date(timeLinePdfData.closedOn)).format(
                                  'DD MMM YYYY, h:mm A'
                                )}
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <br />

                    <TableContainer
                      sx={{
                        width: '100%',
                        overflowX: 'auto',
                        maxHeight: '55vh',
                      }}
                      component={Paper}
                    >
                      <Table
                        className="responsiveTable"
                        stickyHeader
                        aria-label="simple table"
                        size="small"
                      >
                        <TableHead>
                          <TableRow>
                            {tableHeadArr &&
                              tableHeadArr.map((head, i) => (
                                <TableCell
                                  key={'h' + i}
                                  sx={{
                                    padding: '10px',

                                    textAlign: ['Start Date', 'Close Date'].includes(head.name)
                                      ? 'right'
                                      : 'left',
                                  }}
                                >
                                  {head.name}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableDataArr &&
                            tableDataArr.map((tableData, i) => (
                              <TableRow key={'tr' + i}>
                                {tableData &&
                                  tableData.map((td, j) => {
                                    const fieldName = tableHeadArr[j]?.name // Get field name based on index

                                    return (
                                      <TableCell
                                        key={'tc' + j}
                                        sx={{
                                          padding: '10px',
                                          minWidth: '40px',
                                          width: tableData.width + '%', // Set column width dynamically
                                          textAlign: ['Start Date', 'Close Date'].includes(
                                            fieldName
                                          )
                                            ? 'right' // align to right side
                                            : 'left',
                                        }}
                                      >
                                        <span style={{ whiteSpace: 'pre-line' }}>
                                          {tableData[3] == 'active' && j == 5 ? '' : td}
                                        </span>
                                      </TableCell>
                                    )
                                  })}
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </DialogContent>
              ) : viewOptions === 'processflow' ? (
                <DialogContent sx={{ padding: 0, height: '70vh' }}>
                  <ProcessFlowViewer
                    processTemplateId={processTemplateId}
                    processReferanceNum={processReferanceNum}
                    currentProcessData={getMinimalTimelineForFlow(updatedTimeline)} // Minimal filtered data
                    processTemplates={processTemplates}
                    templateRoutes={templateRoutes}
                  />
                </DialogContent>
              ) : (
                <DialogContent
                  className="timeline-cardsection"
                  sx={{ maxHeight: '75vh', width: '100%' }}
                >
                  <ParallelRouteTimeline
                    processReferenceNum={processReferanceNum}
                    processTemplateId={processTemplateId}
                    openProcessData={(dd) => {
                      loadFormDataAndAllValues(dd)
                      setActivityDataOpen(true)
                    }}
                  />
                </DialogContent>
              )}
            </>
          )}
        </Dialog>

        {isDocOpen && (
          <DocumentDialogBox
            isDocOpen={isDocOpen}
            setIsDocOpen={setIsDocOpen}
            setTimelineOpen={setTimelineOpen}
            title={'auto-generated document(s)'}
            docData={docData}
            timelines={updatedTimeline}
            DocStyle={DocStyle}
          />
        )}

        <Dialog
          open={activityDataOpen}
          onClose={() => {
            setActivityDataOpen(false)
          }}
        >
          <DataPreview
            onClose={() => {
              setTimelineOpen(true)
              setActivityDataOpen(false)
            }}
            processId={processId}
            open={!!processId}
          />
        </Dialog>
      </>
      {/* )} */}
    </>
  )
}

export default React.memo(ProcessTimeline)
